import { useState, useEffect, useCallback, useMemo } from 'react';
import { billingService } from '../services/billingService';

/**
 * Calculer les dates de début/fin basées sur une période
 */
const getDateRangeFromPeriod = (periodDays) => {
  if (!periodDays || periodDays === 'all') {
    return { startDate: null, endDate: null };
  }
  
  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - parseInt(periodDays, 10));
  
  return {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  };
};

/**
 * Calculer des statistiques enrichies à partir des transactions
 */
const computeEnrichedStats = (transactions, baseStats, periodDays = 30) => {
  // S'assurer que baseStats est un objet valide
  const safeBaseStats = baseStats || {};
  
  if (!transactions || transactions.length === 0) {
    return {
      totalSpent: 0,
      totalEarned: 0,
      upscaleCount: 0,
      watermarkCount: 0,
      avgDailyUsage: 0,
      daysUntilEmpty: null,
      periodDays,
      spentTrend: safeBaseStats.spentTrend || 0,
      earnedTrend: safeBaseStats.earnedTrend || 0,
      transactionCount: 0,
      ...safeBaseStats,
    };
  }

  let totalSpent = 0;
  let totalEarned = 0;
  let upscaleCount = 0;
  let watermarkCount = 0;

  transactions.forEach((tx) => {
    const amount = tx.amount || 0;
    if (amount < 0) {
      totalSpent += Math.abs(amount);
    } else {
      totalEarned += amount;
    }

    const type = tx.transaction_type?.toUpperCase();
    if (type === 'UPSCALE' || type?.includes('UPSCALE')) {
      upscaleCount++;
    }
    if (type === 'WATERMARK_REMOVAL' || type?.includes('WATERMARK')) {
      watermarkCount++;
    }
  });

  const avgDailyUsage = periodDays > 0 ? Math.round(totalSpent / periodDays) : 0;

  return {
    totalSpent,
    totalEarned,
    upscaleCount,
    watermarkCount,
    avgDailyUsage,
    daysUntilEmpty: null, // Sera calculé avec le solde actuel
    periodDays,
    spentTrend: safeBaseStats.spentTrend || 0,
    earnedTrend: safeBaseStats.earnedTrend || 0,
    transactionCount: transactions.length,
    ...safeBaseStats,
  };
};

/**
 * Générer des données de graphique à partir des transactions
 */
const generateChartData = (transactions, periodDays = 30) => {
  if (!transactions || transactions.length === 0) {
    return [];
  }

  // Trier par date croissante
  const sorted = [...transactions].sort(
    (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
  );

  // Créer des points de données avec balance cumulée
  let runningBalance = 0;
  const data = sorted.map((tx) => {
    runningBalance += tx.amount || 0;
    return {
      timestamp: tx.timestamp,
      balance: tx.balance_after ?? runningBalance,
      amount: tx.amount,
      type: tx.transaction_type,
    };
  });

  return data;
};

export const useCreditHistory = (initialParams = {}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [periodDays, setPeriodDays] = useState(initialParams.periodDays || '30');
  const [params, setParams] = useState({
    limit: 50,
    startAfter: null,
    transactionType: null,
    startDate: null,
    endDate: null,
    ...initialParams
  });

  const fetchHistory = useCallback(async (newParams = null) => {
    try {
      setLoading(true);
      setError(null);
      const currentParams = newParams || params;
      const result = await billingService.getCreditHistory(currentParams);
      
      if (newParams?.startAfter) {
        // Append results for pagination
        setHistory(prev => [...prev, ...(result.transactions || [])]);
      } else {
        // Replace results for new filters
        setHistory(result.transactions || []);
      }
      
      setHasMore(result.hasMore ?? false);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [params]);

  const fetchStats = useCallback(async (days = 30) => {
    try {
      const apiStats = await billingService.getCreditStats(days);
      setStats(apiStats);
    } catch (err) {
      console.error('Error fetching credit stats:', err);
    }
  }, []);

  const loadMore = useCallback(() => {
    if (!loading && hasMore && history.length > 0) {
      const lastItem = history[history.length - 1];
      const newParams = {
        ...params,
        startAfter: lastItem.id
      };
      setParams(newParams);
      fetchHistory(newParams);
    }
  }, [loading, hasMore, history, params, fetchHistory]);

  const updateFilters = useCallback((newFilters) => {
    const updatedParams = {
      ...params,
      ...newFilters,
      startAfter: null // Reset pagination when filters change
    };
    setParams(updatedParams);
    setHistory([]); // Clear history when filters change
    fetchHistory(updatedParams);
  }, [params, fetchHistory]);

  const updateTransactionType = useCallback((type) => {
    updateFilters({ transactionType: type || null });
  }, [updateFilters]);

  const updatePeriod = useCallback((period) => {
    setPeriodDays(period);
    const { startDate, endDate } = getDateRangeFromPeriod(period);
    updateFilters({ startDate, endDate });
    // Also refresh stats for the new period
    fetchStats(parseInt(period, 10) || 30);
  }, [updateFilters, fetchStats]);

  const updateSearchQuery = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  const clearFilters = useCallback(() => {
    setSearchQuery('');
    setPeriodDays('30');
    const clearedParams = {
      limit: 50,
      startAfter: null,
      transactionType: null,
      startDate: null,
      endDate: null,
    };
    setParams(clearedParams);
    setHistory([]);
    fetchHistory(clearedParams);
    fetchStats(30);
  }, [fetchHistory, fetchStats]);

  // Filtrer localement par recherche
  const filteredHistory = useMemo(() => {
    if (!searchQuery.trim()) {
      return history;
    }
    const query = searchQuery.toLowerCase();
    return history.filter((tx) => {
      const description = (tx.description || '').toLowerCase();
      const type = (tx.transaction_type || '').toLowerCase().replace(/_/g, ' ');
      const reference = (tx.reference_id || '').toLowerCase();
      return description.includes(query) || type.includes(query) || reference.includes(query);
    });
  }, [history, searchQuery]);

  // Statistiques enrichies calculées à partir des transactions
  const enrichedStats = useMemo(() => {
    return computeEnrichedStats(history, stats, parseInt(periodDays, 10) || 30);
  }, [history, stats, periodDays]);

  // Données pour le graphique
  const chartData = useMemo(() => {
    return generateChartData(history, parseInt(periodDays, 10) || 30);
  }, [history, periodDays]);

  // Initial load
  useEffect(() => {
    fetchHistory();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Initial stats load
  useEffect(() => {
    fetchStats(parseInt(periodDays, 10) || 30);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    // Data
    history: filteredHistory,
    allHistory: history,
    chartData,
    
    // State
    loading,
    error,
    hasMore,
    
    // Stats
    stats,
    enrichedStats,
    
    // Filters
    searchQuery,
    periodDays,
    transactionType: params.transactionType,
    
    // Actions
    loadMore,
    updateFilters,
    updateTransactionType,
    updatePeriod,
    updateSearchQuery,
    clearFilters,
    refreshHistory: () => {
      setHistory([]);
      fetchHistory({ ...params, startAfter: null });
    },
    refreshStats: () => fetchStats(parseInt(periodDays, 10) || 30),
  };
};
